import { orderBy, pick, uniqBy } from 'lodash-es'
import { ref } from 'vue'

import { applyFont } from '@lib/util/font.util'
import { SUPPORTED_FONT_TYPES } from '@lib/constants/uploads.const'
import { DEFAULT_FONT, type aspectRatios } from '@canvas-builder/constants'
import { FileTypeEnum, type File, type CanvasRetrieve } from '@openapi'
import type { CanvasTheme, ComponentNode, MultimediaProps } from '@canvas-builder/types/canvas.ts'
import useThemeVars from '@lib/composables/useThemeVars'
import type {
  CanvasThemeRetrieve,
  CanvasTypographyPreset,
  CanvasTypographyPresetId,
  CanvasThemeAsset,
} from '@lib/types/canvasTheme'
import { textStylePresets } from '@theme/theme.utils'
import type {
  BackgroundAttachment,
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
} from '@lib/types/background'
import {
  DEFAULT_GLOBAL_COMPONENT_SPACING,
  CANVAS_TYPOGRAPHY_CSS_PROPERTIES,
  CANVAS_TYPOGRAPHY_PRESETS,
} from '@canvas-builder/constants/theme.const'
import useCanvasThemeAssets from '@canvas-builder/composables/useCanvasThemeAssets'

type AvailableAspectRatio = (typeof aspectRatios)[number]

export enum ThemeComponentOptionEnum {
  COLOR_PICKER,
  PADDING,
  SPACING,
  HEIGHT,
  BORDER_RADIUS,
}

export interface ThemePadding {
  top: number
  right: number
  bottom: number
  left: number
}

export interface ThemeBorderRadius {
  topLeft: number
  topRight: number
  bottomLeft: number
  bottomRight: number
}

export interface ThemeOptions {
  backgroundColor?: string
  backgroundAttachment?: BackgroundAttachment
  backgroundImage?: string
  backgroundPositionX?: BackgroundPositionX
  backgroundPositionY?: BackgroundPositionY
  backgroundRepeat?: BackgroundRepeat
  backgroundSize?: BackgroundSize
  height?: string | null
  padding?: ThemePadding
  spacing?: number | null
  borderRadius?: ThemeBorderRadius
}

export interface ThemeUpdateEvents {
  onBackgroundChange: (property: string, value: any) => void
  onHeightChange: (height?: string | null) => void
  onPaddingChange: (padding: ThemePadding) => void
  onMarginBottomChange: (value: number | null) => void
  onResetSpacing: () => void
  onBorderRadiusChange: (borderRadius: ThemeBorderRadius) => void
}

const themeVars = useThemeVars()

export const DEFAULT_TYPOGRAPHY_STYLE: Omit<CanvasTypographyPreset, 'id'> = {
  color: themeVars.value.primary,
  font_family: DEFAULT_FONT,
  font_size: '16px',
  font_weight: '400',
  letter_spacing: '0px',
  line_height: '100%',
  text_transform: 'none',
}
const fontAddedDict: Record<string, boolean> = {}

const activeTheme = ref<CanvasThemeRetrieve | null>(null)
const customFonts = ref<File[]>([])
const isEditingCanvasTheme = ref(false)
const isLoading = ref(false)
const canvasBgColor = ref('')
const palette = ref<string[]>([themeVars.value.primary])
const typography = ref<Record<string, CanvasTypographyPreset>>({})
const logo = ref<CanvasThemeAsset | null>(null)
const assets = ref<CanvasThemeAsset[]>([])
const globalComponentSpacing = ref(DEFAULT_GLOBAL_COMPONENT_SPACING)
const font = ref(DEFAULT_FONT)
const inheritThemeFromParent = ref(false)
const isFluid = ref(false)
const aspectRatio = ref<AvailableAspectRatio>('unlocked')
const maintainSectionTheme = ref(true)
const backgroundAttachment = ref<BackgroundAttachment>('fixed')
const backgroundVideoUrl = ref('')
const backgroundImage = ref('')
const backgroundPositionX = ref<BackgroundPositionX>('center')
const backgroundPositionY = ref<BackgroundPositionY>('center')
const backgroundSize = ref<BackgroundSize>('cover')
const backgroundRepeat = ref<BackgroundRepeat>('no-repeat')
const originalTheme = ref<CanvasThemeRetrieve | null>(null) // this is used to store the original theme when the user is editing a template
const themes = ref<CanvasThemeRetrieve[]>([])
const pdfDimensionsPreset = ref<string>()
const pdfDimensionsWidth = ref<string>()
const pdfDimensionsHeight = ref<string>()

function removeCssPropertiesByPrefix(element: HTMLElement, prefix: string) {
  const style = element.style

  for (let i = style.length - 1; i >= 0; i--) {
    const property = style[i]
    if (property.startsWith(prefix)) {
      style.removeProperty(property)
    }
  }
}

function clearBackgroundColorVariable() {
  const root = document.documentElement
  root.style.removeProperty('--theme-background-color')
}

function cleanup() {
  customFonts.value = []
  isEditingCanvasTheme.value = false
  inheritThemeFromParent.value = false
  isFluid.value = false
  aspectRatio.value = 'unlocked'
  maintainSectionTheme.value = true
  canvasBgColor.value = ''
  palette.value = [themeVars.value.primary]
  logo.value = null
  assets.value = []
  globalComponentSpacing.value = DEFAULT_GLOBAL_COMPONENT_SPACING
  font.value = DEFAULT_FONT
  typography.value = {}
  backgroundAttachment.value = 'fixed'
  backgroundVideoUrl.value = ''
  backgroundImage.value = ''
  backgroundPositionX.value = 'center'
  backgroundPositionY.value = 'center'
  backgroundSize.value = 'auto'
  backgroundRepeat.value = 'no-repeat'
  pdfDimensionsPreset.value = undefined
  pdfDimensionsWidth.value = undefined
  pdfDimensionsHeight.value = undefined

  removeCssPropertiesByPrefix(document.documentElement, '--theme-color-palette-')
  clearBackgroundColorVariable()
}

function getDefaultTypographyConfiguration() {
  return Object.fromEntries(
    textStylePresets.map((style) => [
      style.id,
      {
        id: style.id as CanvasTypographyPresetId,
        font_family: DEFAULT_TYPOGRAPHY_STYLE.font_family,
        font_size: style.style.fontSize,
        font_weight: DEFAULT_TYPOGRAPHY_STYLE.font_weight,
        color: themeVars.value.text,
        line_height: DEFAULT_TYPOGRAPHY_STYLE.line_height,
        letter_spacing: DEFAULT_TYPOGRAPHY_STYLE.letter_spacing,
        text_transform: DEFAULT_TYPOGRAPHY_STYLE.text_transform,
      },
    ]),
  )
}

async function loadCustomFonts(listFiles: (params: any) => Promise<File[]>) {
  const fontFiles = await listFiles({
    filters: { type: [FileTypeEnum.ASSET], content_extension: SUPPORTED_FONT_TYPES },
  })

  customFonts.value = fontFiles?.length ? orderBy(uniqBy(fontFiles, 'name'), ['name']) : []

  for (const customFont of customFonts.value) {
    addAndApplyFont(customFont?.name, customFont?.content_url)
  }
}

async function setFontAndFetchAsset(fontName: string = '', url: string = '') {
  if (!fontName) return
  font.value = fontName

  addAndApplyFont(fontName, url)
}

function addAndApplyFont(fontName?: string, fontUrl?: string | null) {
  if (!fontName && !fontUrl) {
    console.warn('font name and url missing')
    return
  } else if (!fontName) {
    console.warn(`font name missing - ${fontUrl}`)
    return
  } else if (!fontUrl && fontName !== DEFAULT_FONT) {
    console.warn(`font url missing - ${fontName}`)
    return
  }

  if (!fontAddedDict[fontName] && fontName !== DEFAULT_FONT && fontUrl) {
    addDownloadedFont(fontName)
    applyFont(fontUrl, fontName)
  }
}

function applyTheme(theme: CanvasThemeRetrieve | null, legacyCanvasTheme?: Partial<CanvasTheme>) {
  activeTheme.value = theme
  if (!theme) return

  // TODO: keep backward compatibility with legacy theme
  // drop this line once we add the default font to the theme
  // font.value = theme.default_font ?? DEFAULT_FONT
  font.value = legacyCanvasTheme?.font?.name ?? DEFAULT_FONT

  // Update the canvas's fluid property based on the canvas legacy theme
  if (legacyCanvasTheme) {
    updateThemeProperties(
      pick(legacyCanvasTheme, [
        'fluid',
        'aspect_ratio',
        'maintain_section_theme',
        'inherit_theme_from_parent',
        'pdf_dimensions',
      ]),
    )
  }

  // set reactive theme properties
  canvasBgColor.value = theme.settings?.background_color
  globalComponentSpacing.value = theme.settings?.component_spacing
  palette.value = theme.settings?.palette
  typography.value = theme.typography
  logo.value = theme.logo ?? null
  assets.value = theme.assets ?? []

  // set text css variables
  updateTextCSSVariables(theme.typography)
  // set color palette css variables
  updateColorPaletteVariables(theme.settings?.palette || [])
  // set background color variable
  updateBackgroundColorVariable(theme.settings?.background_color || '')
}

function initializeTheme() {
  palette.value = [themeVars.value.primary]
  typography.value = getDefaultTypographyConfiguration()
}

function addDownloadedFont(fontName?: string) {
  if (fontName && !fontAddedDict[fontName]) fontAddedDict[fontName] = true
}

function handleComponentThemeUpdate(updates: ThemeOptions, currentTheme: ThemeOptions, events: ThemeUpdateEvents) {
  const {
    onBackgroundChange,
    onHeightChange,
    onPaddingChange,
    onMarginBottomChange,
    onResetSpacing,
    onBorderRadiusChange,
  } = events

  // Background properties
  const backgroundProps = [
    'backgroundColor',
    'backgroundAttachment',
    'backgroundImage',
    'backgroundPositionX',
    'backgroundPositionY',
    'backgroundRepeat',
    'backgroundSize',
  ] as const

  backgroundProps.forEach((prop) => {
    if (updates[prop] !== currentTheme[prop]) {
      onBackgroundChange(prop, updates[prop])
    }
  })

  // Height
  if (updates.height !== currentTheme.height) {
    onHeightChange(updates.height)
  }

  // Padding
  if (updates.padding !== currentTheme.padding) {
    onPaddingChange(updates.padding as ThemePadding)
  }

  // Border Radius
  if (updates.borderRadius !== currentTheme.borderRadius) {
    onBorderRadiusChange(updates.borderRadius as ThemeBorderRadius)
  }

  // Spacing
  if (updates.spacing !== currentTheme.spacing) {
    if (updates.spacing === undefined) {
      onResetSpacing()
    } else {
      onMarginBottomChange(updates.spacing)
    }
  }
}

function updateThemeProperties(theme: Partial<CanvasTheme>) {
  if (!theme) return
  canvasBgColor.value = theme.canvas_bg_color ?? ''
  if (theme.global_component_spacing) globalComponentSpacing.value = theme.global_component_spacing
  if (theme.font) font.value = theme.font.name
  if (theme.fluid !== undefined) isFluid.value = theme.fluid
  aspectRatio.value = theme.aspect_ratio || 'unlocked'
  maintainSectionTheme.value = theme.maintain_section_theme !== false
  if (theme.inherit_theme_from_parent) inheritThemeFromParent.value = theme.inherit_theme_from_parent
  if (theme.palette) {
    palette.value = theme.palette
    updateColorPaletteVariables(theme.palette)
  }
  if (theme.logo) logo.value = theme.logo
  if (theme.assets) assets.value = theme.assets
  if (theme.typography) {
    typography.value = theme.typography
    updateTextCSSVariables(theme.typography)
  }
  if (theme.background_video_url) backgroundVideoUrl.value = theme.background_video_url
  if (theme.pdf_dimensions) {
    pdfDimensionsPreset.value = theme.pdf_dimensions.preset
    pdfDimensionsWidth.value = theme.pdf_dimensions.width
    pdfDimensionsHeight.value = theme.pdf_dimensions.height
  }

  // Update background color variable
  updateBackgroundColorVariable(theme.canvas_bg_color || '')

  setBackgroundImageProperties(theme.style)
}

function setBackgroundImageProperties(style?: Partial<CanvasTheme['style']>) {
  if (style?.backgroundImage) {
    backgroundAttachment.value = (style.backgroundAttachment as BackgroundAttachment) || 'fixed'
    backgroundImage.value = removeCssUrlFunc(style.backgroundImage)
    backgroundPositionX.value = (style.backgroundPositionX as BackgroundPositionX) || 'center'
    backgroundPositionY.value = (style.backgroundPositionY as BackgroundPositionY) || 'center'
    backgroundSize.value = (style.backgroundSize as BackgroundSize) || 'cover'
    backgroundRepeat.value = (style.backgroundRepeat as BackgroundRepeat) || 'no-repeat'
  } else {
    backgroundAttachment.value = 'fixed'
    backgroundImage.value = ''
    backgroundPositionX.value = 'center'
    backgroundPositionY.value = 'center'
    backgroundSize.value = 'auto'
    backgroundRepeat.value = 'no-repeat'
  }
}

function updateTextCSSVariables(typography: Record<string, CanvasTypographyPreset>) {
  const root = document.documentElement
  if (typography) {
    for (const [key, value] of Object.entries(typography)) {
      CANVAS_TYPOGRAPHY_CSS_PROPERTIES.forEach((prop) => {
        const propKey = prop.replace(/-([a-z])/g, (_, letter) => `_${letter}`) as keyof CanvasTypographyPreset
        const propValue = value[propKey]
        if (propValue) {
          root.style.setProperty(`--theme-${key}-${prop}`, propValue as string)
        }
      })
    }
  }
}

function updateColorPaletteVariables(paletteColors: string[]) {
  const root = document.documentElement

  // Clear existing color palette variables first
  removeCssPropertiesByPrefix(root, '--theme-color-palette-')

  // Set new color palette variables
  paletteColors.forEach((color, index) => {
    root.style.setProperty(`--theme-color-palette-${index}`, color)
  })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function loadSectionThemes(sections: CanvasRetrieve['sections'], specificSection?: CanvasRetrieve['sections'][number]) {
  if (!sections || !sections.length) return

  // Process each section's theme
  sections.forEach((section: any) => {
    if (!section.theme?.typography) return
    if (section.theme?.inherit_theme_from_parent) return

    // If a specific section is provided, only process that one
    if (specificSection && specificSection.id !== section.id) return

    // Apply section-specific theme variables to all instances of this section
    const sectionElements = document.querySelectorAll(`[data-section-id="${section.id}"]`) as NodeListOf<HTMLElement>
    if (!sectionElements.length) return

    const typography = section.theme.typography

    // Apply typography variables to all section elements with this ID
    sectionElements.forEach((sectionElement) => {
      for (const [key, value] of Object.entries(typography)) {
        if (!value) continue

        // Skip if not a valid typography key
        if (!CANVAS_TYPOGRAPHY_PRESETS.includes(key as any)) {
          console.warn(`Skipping invalid typography key: ${key}`)
          continue
        }

        const typographyValue = value as CanvasTypographyPreset

        // Map typography value properties to CSS properties with proper typing
        const propertyMap: Record<string, string | undefined> = {
          'font-family': typographyValue.font_family,
          'font-size': typographyValue.font_size,
          'font-weight': typographyValue.font_weight,
          'line-height': typographyValue.line_height,
          'letter-spacing': typographyValue.letter_spacing,
          'text-transform': typographyValue.text_transform,
          color: typographyValue.color,
        }

        // Set all properties using the standardized pattern
        CANVAS_TYPOGRAPHY_CSS_PROPERTIES.forEach((prop) => {
          const value = propertyMap[prop]
          if (value !== undefined) {
            sectionElement.style.setProperty(`--section-${key}-${prop}`, value || `var(--theme-${key}-${prop})`)
          }
        })
      }
    })
  })
}

function loadSectionThemesAssets(
  sections: CanvasRetrieve['sections'],
  updateMultimediaNodeFn?: (
    nodeId: string,
    props: MultimediaProps,
    directNodeUpdate?: { node: ComponentNode; section: CanvasRetrieve['sections'][number] },
  ) => boolean,
  specificSection?: CanvasRetrieve['sections'][number],
) {
  if (!sections || !sections.length || !updateMultimediaNodeFn) return 0

  const { processThemeAssetsForSections } = useCanvasThemeAssets()

  // Process sections using the unified helper
  return processThemeAssetsForSections(sections, updateMultimediaNodeFn, specificSection)
}

function clearSectionThemes(sections?: CanvasRetrieve['sections'], sectionId?: CanvasRetrieve['sections'][number]) {
  // If a specific section ID is provided, only clear that section (all instances)
  if (sectionId) {
    const sectionElements = document.querySelectorAll(`[data-section-id="${sectionId.id}"]`) as NodeListOf<HTMLElement>
    sectionElements.forEach((sectionElement) => {
      clearSectionElementVariables(sectionElement)
    })
    return
  }

  // If sections are provided, clear only those sections (all instances)
  if (sections && sections.length) {
    sections.forEach((section: any) => {
      const sectionElements = document.querySelectorAll(`[data-section-id="${section.id}"]`) as NodeListOf<HTMLElement>
      sectionElements.forEach((sectionElement) => {
        clearSectionElementVariables(sectionElement)
      })
    })
    return
  }

  // Otherwise, clear all sections in the document
  const allSectionElements = document.querySelectorAll('[data-section-id]')
  allSectionElements.forEach((element) => {
    clearSectionElementVariables(element as HTMLElement)
  })
}

function clearSectionElementVariables(element: HTMLElement) {
  // Get all keys from the typography object by checking existing CSS variables
  // This approach ensures we clear all variables that might have been set
  const computedStyle = window.getComputedStyle(element)
  const cssVarRegex = new RegExp(`--section-(.+?)-(${CANVAS_TYPOGRAPHY_CSS_PROPERTIES.join('|')})`)

  // Collect all typography keys from existing CSS variables
  const typographyKeys = new Set<string>()

  for (let i = 0; i < computedStyle.length; i++) {
    const property = computedStyle[i]
    if (property.startsWith('--section-')) {
      const match = property.match(cssVarRegex)
      if (match && match[1] && CANVAS_TYPOGRAPHY_PRESETS.includes(match[1] as any)) {
        typographyKeys.add(match[1])
      }
    }
  }

  // If no keys were found in computed style, use the predefined valid keys
  if (typographyKeys.size === 0) {
    CANVAS_TYPOGRAPHY_PRESETS.forEach((key: string) => {
      CANVAS_TYPOGRAPHY_CSS_PROPERTIES.forEach((prop: string) => {
        element.style.removeProperty(`--section-${key}-${prop}`)
      })
    })
  } else {
    // Clear all found typography variables
    typographyKeys.forEach((key: string) => {
      CANVAS_TYPOGRAPHY_CSS_PROPERTIES.forEach((prop: string) => {
        element.style.removeProperty(`--section-${key}-${prop}`)
      })
    })
  }
}

function removeCssUrlFunc(url: string) {
  if (!url) return ''
  return url.replace(/^url\(['"]?|['"]?\)$/g, '')
}

function updateBackgroundColorVariable(color: string) {
  const root = document.documentElement
  root.style.setProperty('--theme-background-color', color)
}

export default function useCanvasTheme() {
  return {
    activeTheme,
    applyTheme,
    canvasBgColor,
    clearSectionThemes,
    customFonts,
    font,
    globalComponentSpacing,
    inheritThemeFromParent,
    isEditingCanvasTheme,
    isFluid,
    aspectRatio,
    maintainSectionTheme,
    palette,
    logo,
    assets,
    typography,
    backgroundAttachment,
    backgroundImage,
    backgroundVideoUrl,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
    backgroundRepeat,
    pdfDimensionsPreset,
    pdfDimensionsWidth,
    pdfDimensionsHeight,
    isLoading,
    initializeTheme,
    getDefaultTypographyConfiguration,
    loadCustomFonts,
    setFontAndFetchAsset,
    themes,
    updateTextCSSVariables,
    updateThemeProperties,
    loadSectionThemes,
    loadSectionThemesAssets,
    removeCssUrlFunc,
    cleanup,
    handleComponentThemeUpdate,
    originalTheme,
    setBackgroundImageProperties,
  }
}
